import React, { useEffect } from 'react';
import { MacWindow } from './MacWindow';

interface LegalModalsProps {
  type: 'privacy' | 'terms' | 'contact' | null;
  onClose: () => void;
}

export const LegalModals = ({ type, onClose }: LegalModalsProps) => {
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    if (type) {
      window.addEventListener('keydown', handleKeyDown);
    }
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [type, onClose]);

  if (!type) return null;

  return (
    <MacWindow isOpen={!!type} onClose={onClose} title={type === "privacy" ? "Privacy Policy" : type === "terms" ? "Terms of Service" : "Contact Us"} maxWidth="max-w-2xl">
      <div className="flex flex-col w-full h-full overflow-hidden">
        <div className="flex-1 overflow-y-auto p-6 md:p-8">
          <div className="prose prose-invert prose-zinc max-w-none prose-h2:mt-0 prose-h3:text-zinc-300 prose-p:text-zinc-400 prose-li:text-zinc-400">
            {type === 'privacy' && (
              <>
                <h2 className="text-2xl font-bold text-white mb-4">Privacy Policy</h2>
                <p><strong>Effective Date:</strong> August 31, 2026</p>
                <p>Welcome to PrintScale. We value your privacy and are committed to protecting your personal data. This privacy policy explains how we collect, use, and protect your information when you use our service.</p>
                <h3>1. Information We Collect</h3>
                <p>We collect information you provide directly to us, such as when you create an account, update your profile, or contact customer support. This may include your name, email address, and profile picture.</p>
                <h3>2. How We Use Your Information</h3>
                <p>We use the information we collect to provide, maintain, and improve our services, to communicate with you, and to personalize your experience.</p>
                <h3>3. Sharing of Information</h3>
                <p>We may share the information we collect about you as described in this policy or as described at the time of collection or sharing, including as follows: with third-party service providers to enable them to provide the services you request.</p>
                <h3>4. Data Security</h3>
                <p>We take reasonable measures to help protect information about you from loss, theft, misuse and unauthorized access, disclosure, alteration and destruction.</p>
              </>
            )}

            {type === 'terms' && (
              <>
                <h2 className="text-2xl font-bold text-white mb-4">Terms of Service</h2>
                <p><strong>Effective Date:</strong> August 31, 2026</p>
                <p>By accessing or using PrintScale, you agree to be bound by these Terms of Service and all applicable laws and regulations.</p>
                <h3>1. Use License</h3>
                <p>Permission is granted to temporarily download one copy of the materials (information or software) on PrintScale's website for personal, non-commercial transitory viewing only.</p>
                <h3>2. Disclaimer</h3>
                <p>The materials on PrintScale's website are provided on an 'as is' basis. PrintScale makes no warranties, expressed or implied, and hereby disclaims and negates all other warranties including, without limitation, implied warranties or conditions of merchantability, fitness for a particular purpose, or non-infringement of intellectual property or other violation of rights.</p>
                <h3>3. Limitations</h3>
                <p>In no event shall PrintScale or its suppliers be liable for any damages (including, without limitation, damages for loss of data or profit, or due to business interruption) arising out of the use or inability to use the materials on PrintScale's website.</p>
                <h3>4. Acceptable Use</h3>
                <p>You agree to use the service only for lawful purposes. You agree not to take any action that might compromise the security of the service, render the service inaccessible to others or otherwise cause damage to the service or the Content.</p>
                <h3>5. Intellectual Property</h3>
                <p>All content included on this site, such as text, graphics, logos, button icons, images, and software, is the property of PrintScale or its content suppliers and protected by international copyright laws.</p>
              </>
            )}

            {type === 'contact' && (
              <>
                <h2 className="text-2xl font-bold text-white mb-4">Contact Us</h2>
                <p>We'd love to hear from you. Please reach out to us using the details below for any support or technical issues.</p>
                
                <div className="bg-zinc-900 border border-zinc-800 rounded-lg p-6 mt-6">
                  <h4 className="text-lg font-semibold text-white mb-4">Customer Support</h4>
                  <ul className="space-y-4 text-zinc-300">
                    <li className="flex items-center gap-3">
                      <span className="w-8 h-8 rounded-full bg-zinc-800 flex items-center justify-center shrink-0">📧</span>
                      <div>
                        <div className="text-xs text-zinc-500">Email Address</div>
                        <div>support@label-maker.ai.studio</div>
                      </div>
                    </li>
                    <li className="flex items-center gap-3">
                      <span className="w-8 h-8 rounded-full bg-zinc-800 flex items-center justify-center shrink-0">📞</span>
                      <div>
                        <div className="text-xs text-zinc-500">Phone Number</div>
                        <div>+91 708 615 9179</div>
                        <div className="text-[10px] text-zinc-500">Mon-Sat, 9:00 AM - 8:00 PM IST</div>
                      </div>
                    </li>
                    <li className="flex items-center gap-3">
                      <span className="w-8 h-8 rounded-full bg-zinc-800 flex items-center justify-center shrink-0">🏢</span>
                      <div>
                        <div className="text-xs text-zinc-500">Registered Office</div>
                        <div className="text-sm font-medium text-zinc-200">
                          CreatechPack<br/>
                          Udharbond, Silchar, Assam - 788030, India
                        </div>
                      </div>
                    </li>
                  </ul>
                </div>
              </>
            )}
          </div>
        </div>

        <div className="p-4 border-t border-zinc-800 bg-zinc-950 flex justify-end shrink-0">
          <button 
            onClick={onClose}
            className="px-6 py-2 bg-zinc-100 hover:bg-white text-zinc-900 text-sm font-bold rounded shadow-sm transition-colors cursor-pointer"
          >
            Close
          </button>
        </div>
      </div>
    </MacWindow>
  );
};

